import React from 'react';
import useCitedHighlights from '../../evidenceModal/useCitedHighlights';
import useFragmentMarks from '../../evidenceModal/useFragmentMarks';
import {useYSourcePackage} from '../../sourcePackage/hooks';

// Stable empty array reference to avoid re-renders
const EMPTY_ARRAY = [];

// Builds the highlights shown in DocumentContent.
// - Cited passages are shown in green (highest priority).
// - Source package items that are NOT already cited are shown in blue.
export default function useDocumentHighlights({reportId, currentDocKey, docSegments}) {
    const {citedHighlights, addOptimisticCited} = useCitedHighlights(reportId, currentDocKey, docSegments);
    const fragmentMarks = useFragmentMarks();

    // Source package items and folders (from Yjs)
    const {items: spItemsFromYjs, folders: spFolders} = useYSourcePackage();
    const spItems = spItemsFromYjs?.length > 0 ? spItemsFromYjs : EMPTY_ARRAY;

    // Build folder name lookup from YJS folders
    const getFolderNameById = React.useCallback(
        (folderId) => {
            if (!folderId) return 'Root';
            const folder = (spFolders || []).find((f) => f.id === folderId);
            return folder?.name ?? 'Unknown folder';
        },
        [spFolders],
    );

    const labeledHighlights = React.useMemo(() => {
        const byUid = new Map((fragmentMarks || []).map((f) => [f.uid, f.text || '']));
        const truncate = (s = '') => (s.length > 48 ? s.slice(0, 48) + '…' : s);

        // Build cited highlights (green)
        const citedItems = (citedHighlights || [])
            .filter((h) => h?.start && h?.end)
            .map((h) => {
                const fragText = h.fragmentUid ? (byUid.get(h.fragmentUid) || '') : '';
                const label = fragText ? `Cited: ${truncate(fragText)}` : 'Cited';
                return {...h, labelText: label};
            });

        // Set of localIds already covered by a cited highlight — used to suppress
        // duplicate SP highlights for the same passage.
        const citedLocalIds = new Set(citedItems.map(h => h.localId).filter(Boolean));

        // Build source package highlights (blue) — skip items already cited
        const packageItems = (spItems || [])
            .filter((it) => {
                if (it?.docId == null) return false;
                // eslint-disable-next-line eqeqeq
                if (it.docId != currentDocKey) return false;
                // Suppress if the same passage is already shown as cited
                if (it.localId && citedLocalIds.has(it.localId)) return false;
                return true;
            })
            .map((it) => {
                const start = it.start ?? (it.index != null
                    ? {segmentIndex: it.index, segmentOffset: it.offset ?? 0}
                    : null);
                const end = it.end ?? (it.index != null
                    ? {segmentIndex: it.index, segmentOffset: it.offset ?? 0}
                    : null);
                if (!start || !end) return null;

                const folderName = getFolderNameById(it.parentFolderId ?? null);
                const labelText = `In package — ${folderName}`;

                return {
                    ...it,
                    start,
                    end,
                    type: 'sourcePackage',
                    labelText,
                };
            })
            .filter(Boolean);

        // Cited items last so they render on top (highest CSS priority via typePriority in TextSegment)
        return [...packageItems, ...citedItems];
    }, [spItems, citedHighlights, fragmentMarks, currentDocKey, getFolderNameById]);

    return {
        labeledHighlights,
        citedHighlights,
        addOptimisticCited,
        fragmentMarks,
    };
}
